// components/VideoModal.js
import React, { useRef, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Play, Pause, Volume2, VolumeX, Maximize, RotateCcw, Settings } from 'lucide-react';
import { saveWatchProgress, getWatchProgress } from '../mock/movies';

const VideoModal = ({ 
  isOpen, 
  onClose, 
  videoSrc, 
  title, 
  currentProgress = 0,
  onProgress,
  movieId 
}) => {
  const videoRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [progress, setProgress] = useState(0);
  const [hasBeenInitialized, setHasBeenInitialized] = useState(false);
  const [showControls, setShowControls] = useState(true);
  const [controlsTimeout, setControlsTimeout] = useState(null);

  // Mostrar/ocultar controles automáticamente
  const resetControlsTimeout = () => {
    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
    const timeout = setTimeout(() => {
      if (isPlaying) setShowControls(false);
    }, 3000);
    setControlsTimeout(timeout);
  };

  useEffect(() => {
    if (isOpen) {
      resetControlsTimeout();
    }
    return () => {
      if (controlsTimeout) clearTimeout(controlsTimeout);
    };
  }, [isOpen, isPlaying]);

  // Resetear estado cuando se abre un nuevo video
  useEffect(() => {
    if (isOpen && movieId) {
      const movieProgress = getWatchProgress(movieId);
      setProgress(movieProgress);
      setHasBeenInitialized(false);
      setIsPlaying(false);
      setShowControls(true);
    }
  }, [isOpen, movieId]);

  // Inicializar el video cuando esté listo
  useEffect(() => {
    if (videoRef.current && isOpen && !hasBeenInitialized) {
      const initializeVideo = () => {
        const totalDuration = videoRef.current.duration;
        if (totalDuration > 0) {
          setDuration(totalDuration);
          const targetTime = (progress / 100) * totalDuration;
          videoRef.current.currentTime = targetTime;
          setCurrentTime(targetTime);
          setHasBeenInitialized(true);
        }
      };

      if (videoRef.current.readyState >= 2) {
        initializeVideo();
      } else {
        videoRef.current.addEventListener('loadedmetadata', initializeVideo);
        return () => {
          videoRef.current?.removeEventListener('loadedmetadata', initializeVideo);
        };
      }
    }
  }, [isOpen, progress, hasBeenInitialized]);

  const handleTimeUpdate = () => {
    if (videoRef.current && hasBeenInitialized) {
      const current = videoRef.current.currentTime;
      const total = videoRef.current.duration;
      
      if (total > 0) {
        setCurrentTime(current);
        setDuration(total);
        
        const newProgress = (current / total) * 100;
        setProgress(newProgress);
        
        if (movieId && Math.floor(current) % 5 === 0 && newProgress < 95) {
          saveWatchProgress(movieId, Math.floor(newProgress));
          if (onProgress) {
            onProgress(movieId, Math.floor(newProgress));
          }
        }
      }
    }
  };

  const handlePlayPause = () => {
    if (videoRef.current) {
      if (isPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
      setIsPlaying(!isPlaying);
    }
    resetControlsTimeout();
  };

  const handleSeek = (e) => {
    if (videoRef.current && duration > 0) {
      const rect = e.currentTarget.getBoundingClientRect();
      const percent = (e.clientX - rect.left) / rect.width;
      const seekTime = percent * duration;
      videoRef.current.currentTime = seekTime;
      setCurrentTime(seekTime);
    }
    resetControlsTimeout();
  };

  const handleClose = () => {
    if (movieId && progress > 0 && progress < 95) {
      saveWatchProgress(movieId, Math.floor(progress));
      if (onProgress) {
        onProgress(movieId, Math.floor(progress));
      }
    }
    
    setIsPlaying(false);
    setHasBeenInitialized(false);
    setShowControls(true);
    if (controlsTimeout) clearTimeout(controlsTimeout);
    onClose();
  };

  const handleVideoEnd = () => {
    setIsPlaying(false);
    setShowControls(true);
    if (movieId) {
      saveWatchProgress(movieId, 100);
      if (onProgress) {
        onProgress(movieId, 100);
      }
      setProgress(100);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return "0:00";
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleVideoClick = () => {
    handlePlayPause();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95 backdrop-blur-lg"
          onClick={handleClose}
          onMouseMove={resetControlsTimeout}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            transition={{ type: "spring", damping: 25 }}
            className="relative w-full max-w-7xl mx-4 bg-black rounded-3xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header con controles superiores */}
            <AnimatePresence>
              {showControls && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  className="absolute top-0 left-0 right-0 z-20 p-6 bg-gradient-to-b from-black/90 via-black/50 to-transparent"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h2 className="text-white text-2xl font-bold truncate">{title}</h2>
                      {progress > 0 && progress < 100 && (
                        <motion.div 
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          className="flex items-center gap-2 mt-2"
                        >
                          <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
                          <span className="text-green-400 text-sm font-medium">
                            Continuando desde {Math.floor(progress)}%
                          </span>
                        </motion.div>
                      )}
                    </div>
                    
                    <div className="flex items-center gap-3">
                      {/* Indicador de progreso en header */}
                      {progress > 0 && (
                        <div className="hidden md:flex items-center gap-2 px-3 py-1 bg-black/60 rounded-full">
                          <RotateCcw className="w-4 h-4 text-green-400" />
                          <span className="text-white text-sm font-medium">
                            {Math.floor(progress)}%
                          </span>
                        </div>
                      )}
                      
                      <motion.button
                        onClick={handleClose}
                        className="p-3 bg-black/60 rounded-xl text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                        whileHover={{ scale: 1.05, backgroundColor: 'rgba(255,255,255,0.1)' }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <X className="w-6 h-6" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Video Container */}
            <div 
              className="relative w-full h-[80vh] bg-black cursor-pointer"
              onClick={handleVideoClick}
              onDoubleClick={() => {
                if (videoRef.current?.requestFullscreen) {
                  videoRef.current.requestFullscreen();
                }
              }}
            >
              <video
                ref={videoRef}
                className="w-full h-full object-contain"
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={() => {
                  if (videoRef.current) {
                    setDuration(videoRef.current.duration);
                  }
                }}
                onPlay={() => setIsPlaying(true)}
                onPause={() => setIsPlaying(false)}
                onEnded={handleVideoEnd}
                // Prevenir descargas
                controlsList="nodownload noplaybackrate"
                disablePictureInPicture
                style={{ userSelect: 'none' }}
              >
                <source src={videoSrc} type="video/mp4" />
                Tu navegador no soporta el elemento video.
              </video>

              {/* Overlay de reproducción/pausa */}
              <AnimatePresence>
                {!isPlaying && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.8 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm"
                  >
                    <motion.div
                      className="p-6 bg-white/20 rounded-full backdrop-blur-md border border-white/30"
                      whileHover={{ scale: 1.1 }}
                      whileTap={{ scale: 0.9 }}
                    >
                      <Play className="w-16 h-16 text-white ml-2" />
                    </motion.div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Controls inferiores */}
            <AnimatePresence>
              {showControls && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: 20 }}
                  className="absolute bottom-0 left-0 right-0 z-20 p-6 bg-gradient-to-t from-black/90 via-black/60 to-transparent"
                >
                  {/* Progress Bar mejorada */}
                  <div 
                    className="group relative w-full h-3 bg-gray-800/80 rounded-full mb-6 cursor-pointer backdrop-blur-sm"
                    onClick={handleSeek}
                  >
                    <div 
                      className="absolute h-3 bg-red-500 rounded-full transition-all duration-100 group-hover:bg-red-400"
                      style={{ width: `${progress}%` }}
                    />
                    <div 
                      className="absolute h-3 bg-gray-500/50 rounded-full transition-all duration-100"
                      style={{ width: `${progress}%` }}
                    />
                    <div className="absolute w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transition-all duration-200 transform -translate-y-0.5"
                         style={{ left: `calc(${progress}% - 8px)`, top: '50%', transform: 'translateY(-50%)' }} />
                  </div>

                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      {/* Play/Pause */}
                      <motion.button
                        onClick={handlePlayPause}
                        className="p-3 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isPlaying ? 
                          <Pause className="w-6 h-6" /> : 
                          <Play className="w-6 h-6" />
                        }
                      </motion.button>

                      {/* Volume */}
                      <motion.button
                        onClick={() => {
                          if (videoRef.current) {
                            videoRef.current.muted = !isMuted;
                            setIsMuted(!isMuted);
                          }
                        }}
                        className="p-3 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        {isMuted ? 
                          <VolumeX className="w-6 h-6" /> : 
                          <Volume2 className="w-6 h-6" />
                        }
                      </motion.button>

                      {/* Time Display */}
                      <div className="flex items-center gap-2 px-4 py-2 bg-black/40 rounded-lg backdrop-blur-sm">
                        <span className="text-white text-sm font-mono font-medium">
                          {formatTime(currentTime)}
                        </span>
                        <span className="text-gray-400">/</span>
                        <span className="text-gray-400 text-sm font-mono">
                          {formatTime(duration)}
                        </span>
                      </div>
                    </div>

                    <div className="flex items-center gap-3">
                      {/* Progress Indicator */}
                      {progress > 0 && progress < 100 && (
                        <motion.div 
                          className="hidden sm:flex items-center gap-2 px-4 py-2 bg-black/40 rounded-lg backdrop-blur-sm"
                          initial={{ opacity: 0, x: 10 }}
                          animate={{ opacity: 1, x: 0 }}
                        >
                          <RotateCcw className="w-4 h-4 text-green-400" />
                          <span className="text-green-400 text-sm font-medium">
                            {Math.floor(progress)}% visto
                          </span>
                        </motion.div>
                      )}

                      {/* Fullscreen */}
                      <motion.button
                        onClick={() => {
                          if (videoRef.current?.requestFullscreen) {
                            videoRef.current.requestFullscreen();
                          }
                        }}
                        className="p-3 bg-white/10 rounded-xl text-white hover:bg-white/20 transition-all duration-200 backdrop-blur-sm"
                        whileHover={{ scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                      >
                        <Maximize className="w-6 h-6" />
                      </motion.button>
                    </div>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default VideoModal;